/**
 * Every alert BlueHQ can send. An agency picks who gets each one and how (email, text or both); every alert
 * also shows on the person's Home. Some go to certain people automatically no matter what (noted in `always`).
 */
export const ALERT_KINDS = {
  critical: {
    label: 'Critical incident notices',
    about: 'A deputy sends a critical incident notice — a shooting, pursuit, death, or anything the Sheriff and command staff should know about right away.',
    always: null,
  },
  narcan: {
    label: 'Narcan used',
    about: 'A report says Narcan (naloxone) was used. For whoever replaces the deputy’s Narcan, and anyone who tracks overdoses.',
    always: 'The deputy’s supervisor is asked to verify the Narcan form was completed.',
  },
  juvenile: {
    label: 'Juvenile involved',
    about: 'Someone under 18 is on a report or supplement in any role — victim, suspect, witness or anyone else.',
    always: null,
  },
  custody_warrant: {
    label: 'Warrant request — person in custody',
    about: 'A deputy asks for a warrant for someone who is already in custody. These go to the top of the magistrate’s queue in red.',
    always: null,
  },
  inactive: {
    label: 'Hasn’t signed in for 10 days',
    about: 'An employee hasn’t signed in to BlueHQ in 10 days or more.',
    always: 'Their supervisor always sees it on Home.',
  },
  uof_serious: {
    label: 'Serious use of force',
    about: 'A use-of-force report with an injury, a firearm, or other serious force is filed.',
    always: 'Command staff who review use of force see it on Home.',
  },
  escort: {
    label: 'Funeral escort request',
    about: 'A funeral home sends an escort request (from the Jotform), or the office enters one.',
    always: 'Every request shows on everyone’s Home; the deputies put on an escort get it on their Home and calendar.',
  },
  bolo: {
    label: 'New BOLO',
    about: 'A deputy puts out a BOLO (a vehicle or person to look for).',
    always: 'Every active BOLO shows on everyone’s Home and under BOLOs.',
  },
  applicant: {
    label: 'New job application',
    about: 'An application comes in from the employment application form (or HR enters one).',
    always: 'Whoever has the next step on an applicant sees it on their Home.',
  },
  foia: {
    label: 'New FOIA request',
    about: 'A FOIA request is entered.',
    always: 'Whoever has the next FOIA step sees it on their Home.',
  },
  standby_page: {
    label: 'Standby paged for an event',
    about: 'Command staff page the deputies on standby for an event.',
    always: 'The standby deputies themselves always get the page by text, email and on Home.',
  },
  school_safety: {
    label: 'School safety concern',
    about: 'An SRO reports a safety concern at a school (a door that won’t latch, a camera out…).',
    always: 'The school’s SROs see open concerns on the Schools page.',
  },
  ia_referral: {
    label: 'Body cam review referred to IA',
    about: 'A supervisor’s body cam review refers the officer to Internal Affairs / Professional Standards.',
    always: 'Command staff see it in the monthly body cam report.',
  },
  car_assignment: {
    label: 'Car assignment changed',
    about: 'A patrol car is assigned to someone else, taken back, or a deputy takes or returns a spare — so E-911 can change it in CAD.',
    always: 'Everyone in dispatch sees it on Home; outside E-911 contacts (Agency → Alerts → Outside contacts) get an email.',
  },
  command_board: {
    label: 'Command board started',
    about: 'A supervisor (or deputy) starts a command board — bush bond, perimeter, search — and alerts who’s on duty or on standby.',
    always: 'The deputies alerted always get it by text, email and on Home.',
  },
  tip: {
    label: 'New anonymous tip',
    about: 'A tip comes in from the public tip page or the tip line form.',
    always: 'Whoever works tips sees it in the tip queue.',
  },
} as const;
export type AlertKind = keyof typeof ALERT_KINDS;
export const ALERT_KIND_KEYS = Object.keys(ALERT_KINDS) as AlertKind[];
/** Days without signing in before the "hasn't signed in" alert. */
export const INACTIVE_DAYS = 10;
